import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { company, services } from '../data/common';
import { usePackages } from '../../modules/packages/PackagesContext';
import Icon from './Icons';
import { useAuth } from '../../auth/AuthContext';
import CurrencySelect from '../currency/CurrencySelect';
import { firstAdminPath, hasBackOffice } from '../../modules/admin/access';

export function SmartImg({ src, alt, className }) {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    return <div className={`img-fallback ${className || ''}`} role="img" aria-label={alt}><Icon name="image" size={32} /></div>;
  }
  return <img src={src} alt={alt} className={className} loading="lazy" onError={() => setFailed(true)} />;
}

export function Logo({ light }) {
  return (
    <Link to="/" className={`logo ${light ? 'light' : ''}`}>
      <svg className="logo-mark" viewBox="0 0 40 40" aria-hidden="true">
        <defs>
          <linearGradient id="lg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#12b5a6" /><stop offset="1" stopColor="#3fa9f5" />
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="11" fill="url(#lg)" />
        <circle cx="31" cy="9" r="4" fill="#ffc94a" />
        <path transform="translate(8 8)" fill="none" stroke="#fff" strokeWidth="2" strokeLinejoin="round"
          d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
      </svg>
      <span className="logo-text">traw<b>tel</b></span>
    </Link>
  );
}

// Header order: products, then one Packages menu with India and International columns (group and religious tours appear on the home page).
const headerProductKeys = ['flights', 'hotels', 'transfers', 'sightseeing', 'insurance','bus'];
const headerMenuKeys = ['india', 'international'];

/** One account button for My Trips, login and sign-up (or sign-out when signed in). */
function AccountMenu({ user, logout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="account-menu" ref={ref}>
      <button type="button" className="account-btn" aria-haspopup="true" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="account-avatar"><Icon name="user" size={18} /></span>
        <span className="account-label">
          <b>{user ? `Hi, ${user.firstName}` : 'Login / Sign up'}</b>
          <small>My Trips & account</small>
        </span>
        <Icon name="down" size={14} />
      </button>
      {open && (
        <div className="account-dropdown" onClick={(e) => e.target.closest('a') && setOpen(false)}>
          {user ? (
            <>
              <div className="account-dropdown-head"><b>{user.firstName} {user.lastName}</b><small>{user.email}</small></div>
              <Link to="/my-trips"><Icon name="briefcase" size={16} /> My Trips <small>Manage bookings</small></Link>
              {hasBackOffice(user) && <Link to={firstAdminPath(user)}><Icon name="lock" size={16} /> {user.roles?.includes('Admin') ? 'Admin' : 'Staff panel'}</Link>}
              <button type="button" onClick={() => { setOpen(false); logout(); }}><Icon name="arrow" size={16} /> Sign out</button>
            </>
          ) : (
            <>
              <div className="account-dropdown-head"><b>Welcome to Trawtel</b><small>Sign in to manage your bookings</small></div>
              <div className="account-dropdown-auth">
                <Link to="/account/login" className="btn btn-primary btn-sm">Login</Link>
                <Link to="/account/register" className="btn btn-outline btn-sm">Sign up</Link>
              </div>
              <Link to="/my-trips"><Icon name="briefcase" size={16} /> My Trips <small>Manage bookings</small></Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null); // submenu opened by tap (mobile)
  const [dismissed, setDismissed] = useState(null); // hide hover menu after a click until the pointer leaves
  const { user, logout } = useAuth();
  const { menus } = usePackages();
  const { pathname, search } = useLocation();
  const here = pathname + decodeURIComponent(search);
  const packageMenus = headerMenuKeys.map((key) => menus.find((m) => m.key === key)).filter(Boolean);
  const isActive = (m) => [m.to, ...m.items.map((i) => i.to)].some((to) => here === to || here.startsWith(`${to}&`));
  const close = (key) => {
    setOpen(false);
    setExpanded(null);
    setDismissed(key);
    document.activeElement?.blur();
  };
  return (
    <header className="header">
      <div className="topbar">
        <div className="container topbar-inner">
          <span className="topbar-left"><Icon name="tag" size={14} /> Use code <b>FLYFRESH</b> for 12% off your first flight</span>
          <div className="topbar-right">
            <a href="#support"><Icon name="headset" size={14} /> 24x7 Support</a>
            <Link to="/my-trips"><Icon name="briefcase" size={14} /> Manage Booking</Link>
            <CurrencySelect />
          </div>
        </div>
      </div>
      <div className="header-bar">
        <div className="container header-inner">
          <Logo light />
          <nav className={`nav ${open ? 'open' : ''}`}>
            {headerProductKeys.map((key) => services.find((s) => s.key === key)).map((s) => (
              <NavLink key={s.key} to={s.path} onClick={() => close(null)}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                <span className="nav-chip"><Icon name={s.key} size={22} strokeWidth={1.6} /></span>
                <span className="nav-label">{s.label}</span>
              </NavLink>
            ))}
            {packageMenus.length > 0 && (
              <div onMouseLeave={() => setDismissed(null)}
                className={`nav-item ${expanded === 'packages' ? 'expanded' : ''} ${dismissed === 'packages' ? 'dismissed' : ''}`}>
                <Link to="/packages/results" onClick={() => close('packages')}
                  className={`nav-link ${packageMenus.some(isActive) ? 'active' : ''}`}>
                  <span className="nav-chip"><Icon name="globe" size={22} strokeWidth={1.6} /></span>
                  <span className="nav-label">Packages <Icon name="down" size={12} /></span>
                </Link>
                <button className="nav-toggle" aria-label="Show Packages" aria-expanded={expanded === 'packages'}
                  onClick={() => setExpanded(expanded === 'packages' ? null : 'packages')}>
                  <Icon name="down" size={18} />
                </button>
                <div className="nav-menu nav-menu-wide">
                  {packageMenus.map((m) => (
                    <div key={m.key} className="nav-menu-col">
                      <Link to={m.to} onClick={() => close('packages')} className="nav-menu-head">
                        <Icon name={m.icon} size={16} /> {m.label}
                      </Link>
                      {m.items.map((i) => (
                        <Link key={i.to} to={i.to} onClick={() => close('packages')}>
                          <span>{i.label}</span>{i.meta && <small>{i.meta}</small>}
                        </Link>
                      ))}
                      <Link to={m.to} onClick={() => close('packages')} className="nav-menu-all">View all <Icon name="arrow" size={14} /></Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </nav>
          <AccountMenu user={user} logout={logout} />
          <button className="menu-btn" onClick={() => setOpen(!open)} aria-label="Toggle menu">
            <Icon name={open ? 'close' : 'menu'} size={24} />
          </button>
        </div>
      </div>
    </header>
  );
}

const payments = ['VISA', 'Mastercard', 'RuPay', 'AMEX', 'UPI', 'Net Banking'];

export function Footer() {
  return (
    <footer className="footer">
      <div className="container footer-grid">
        <div>
          <Logo light />
          <p className="footer-text">Trawtel is your one-stop travel companion for flights, hotels, holidays, activities, transfers, bus tickets and travel insurance.</p>
          <div className="socials">
            {['facebook', 'instagram', 'youtube', 'linkedin'].map((s) => (
              <a key={s} href={`#${s}`} aria-label={s}><Icon name={s} size={18} /></a>
            ))}
          </div>
        </div>
        <div>
          <h4>Our Products</h4>
          {services.map((s) => <Link key={s.key} to={s.path}>{s.label}</Link>)}
        </div>
        <div>
          <h4>Company</h4>
          <a href="#about">About Us</a><a href="#careers">Careers</a><a href="#blog">Travel Blog</a><a href="#partners">Partner With Us</a><a href="#press">Press Room</a>
        </div>
        <div>
          <h4>Support</h4>
          <a href="#help">Help Centre</a><Link to="/my-trips">Manage Booking</Link><Link to="/my-trips">Refund Status</Link><Link to="/cancellation-policy">Cancellation Policy</Link>
          <a href={company.phoneHref} className="footer-phone"><Icon name="phone" size={14} /> {company.phone}</a>
          <a href={`mailto:${company.supportEmail}`} className="footer-phone"><Icon name="mail" size={14} /> {company.supportEmail}</a>
        </div>
      </div>
      <div className="container footer-pay">
        <span>We accept</span>
        <div className="pay-badges">{payments.map((p) => <span key={p}>{p}</span>)}</div>
        <span className="footer-cert"><Icon name="lock" size={14} /> 256-bit SSL secured · IATA accredited</span>
      </div>
      <div className="container footer-bottom">
        <span>© {new Date().getFullYear()} {company.name}. All rights reserved.</span>
        <span><Link to="/privacy-policy">Privacy</Link> · <Link to="/terms">Terms of Use</Link> · <Link to="/privacy-policy#cookies">Cookies</Link></span>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <>
      <Header />
      <main><Outlet /></main>
      <Footer />
    </>
  );
}
